import { isTestGrade, longDate } from "../legal/notice";
import { subjectsFor } from "../types";
import type { Child, Entry, Material, Program } from "../types";
import { realEntriesFor } from "./gaps";

export interface ReportInput {
  child: Child;
  entries: Entry[];
  program: Program;
  parentName: string;
}

interface SubjectTally {
  count: number;
  minutes: number;
  samples: Entry[];
}

interface Tally {
  bySubject: Map<string, SubjectTally>;
  untagged: number;
  minutes: number;
  days: number;
}

const MAX_SAMPLES = 3;
const MAX_SAMPLE_CHARS = 150;
const WRAP_WIDTH = 68;
const INDENT = "    ";

export function biblio(m: Material): string {
  let out = "";
  if (m.author) out += `${m.author}. `;
  if (m.title) out += `${m.title}. `;
  if (m.publisher) out += m.publisher;
  if (m.publisher && m.year) out += ", ";
  if (m.year) out += m.year;
  if (m.publisher || m.year) out += ".";
  return out.trim();
}

/** Greedy word wrap. `width` counts characters after the indent. */
export function wrap(text: string, width: number, indent: string): string {
  const lines: string[] = [];
  let current = indent;
  for (const word of text.split(" ")) {
    if ((current + word).length > width + indent.length && current !== indent) {
      lines.push(current.trimEnd());
      current = indent;
    }
    current += `${word} `;
  }
  if (current.trim()) lines.push(current.trimEnd());
  return lines.join("\n");
}

function collapseSpaces(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function shortDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function localTime(isoDate: string, clock: string, fallback: number): number {
  if (!isoDate) return fallback;
  const ts = new Date(`${isoDate}T${clock}`).getTime();
  return Number.isNaN(ts) ? fallback : ts;
}

function entriesInProgram({ child, entries, program }: ReportInput): Entry[] {
  const fromTs = localTime(program.from, "00:00:00", -Infinity);
  const toTs = localTime(program.to, "23:59:59.999", Infinity);
  return realEntriesFor(child.id, entries)
    .filter((e) => e.ts >= fromTs && e.ts <= toTs)
    .sort((a, b) => a.ts - b.ts);
}

function tallyFor(bySubject: Map<string, SubjectTally>, subject: string): SubjectTally {
  const existing = bySubject.get(subject);
  if (existing) return existing;
  const fresh: SubjectTally = { count: 0, minutes: 0, samples: [] };
  bySubject.set(subject, fresh);
  return fresh;
}

function tally(list: readonly Entry[]): Tally {
  const bySubject = new Map<string, SubjectTally>();
  const days = new Set<string>();
  let untagged = 0;
  let minutes = 0;
  for (const e of list) {
    minutes += e.minutes;
    days.add(new Date(e.ts).toDateString());
    if (e.subjects.length === 0) untagged += 1;
    for (const subject of e.subjects) {
      const t = tallyFor(bySubject, subject);
      t.count += 1;
      t.minutes += e.minutes / e.subjects.length;
      if (t.samples.length < MAX_SAMPLES && e.text) t.samples.push(e);
    }
  }
  return { bySubject, untagged, minutes, days: days.size };
}

function hoursPerWeek({ minutes, days }: Tally): string {
  if (!minutes) return "[not recorded]";
  const weeks = Math.max(1, days / 5);
  return (minutes / 60 / weeks).toFixed(1);
}

function headerLines({ child, program, parentName }: ReportInput, t: Tally): string[] {
  const student = child.legalName.trim() || child.firstName.trim();
  const preparedBy = parentName.trim() || "[parent name]";
  return [
    "ANNUAL REPORT OF HOME SCHOOL PROGRESS",
    "Hawai‘i Administrative Rules, Title 8, Chapter 12, §8-12-18",
    "",
    `Student            ${student}`,
    `Grade level        ${child.grade}`,
    `Program dates      ${longDate(program.from) || "[start]"} to ${longDate(program.to) || "[end]"}`,
    `Instruction days   ${t.days} days on which instruction was recorded`,
    `Hours per week     ${hoursPerWeek(t)} hours per week, averaged over the program`,
    `Prepared by        ${preparedBy}, parent and qualified instructor under §8-12-19`,
    "",
  ];
}

function sampleLine(e: Entry): string {
  const text = collapseSpaces(e.text);
  const shown = text.length > MAX_SAMPLE_CHARS ? `${text.slice(0, MAX_SAMPLE_CHARS - 3)}...` : text;
  return `        - ${shortDate(e.ts)}: ${shown}`;
}

function subjectBlock(name: string, t: SubjectTally): string[] {
  return [
    INDENT + name,
    `        ${t.count} recorded sessions, approximately ${(t.minutes / 60).toFixed(1)} hours.`,
    ...t.samples.map(sampleLine),
    "",
  ];
}

function subjectLines(child: Child, t: Tally): string[] {
  const lines = ["1.  SUBJECT AREAS COVERED", ""];
  const blocks = subjectsFor(child.grade).flatMap((subject) => {
    const subjectTally = t.bySubject.get(subject);
    return subjectTally ? subjectBlock(subject, subjectTally) : [];
  });
  lines.push(...(blocks.length > 0 ? blocks : ["    No subjects were tagged in the log for this period.", ""]));
  if (t.untagged > 0) {
    lines.push("    Untagged work", `        ${t.untagged} recorded sessions not assigned to a subject.`, "");
  }
  return lines;
}

function masteryLines(program: Program): string[] {
  const mastery = collapseSpaces(program.mastery);
  return [
    "2.  METHOD USED TO DETERMINE MASTERY",
    "",
    mastery
      ? wrap(mastery, WRAP_WIDTH, INDENT)
      : "    [Describe how you determine mastery. Fill this in on the Program tab.]",
    "",
  ];
}

function materialLines(program: Program): string[] {
  const listed = program.materials.map((m) => INDENT + biblio(m));
  return [
    "3.  INSTRUCTIONAL MATERIALS",
    "",
    ...(listed.length > 0 ? listed : ["    [List texts and resources in bibliographic format on the Program tab.]"]),
    "",
  ];
}

const GRADE_10_NOTE = [
  "",
  "    Note: HIDOE’s February 2026 FAQ states that public school students are",
  "    tested in grade 11 rather than grade 10, while Chapter 12 has not been",
  "    amended. Confirm in writing with the principal which year applies.",
] as const;

const PARENT_EVALUATION = [
  "    This report is submitted as the parent’s written evaluation permitted by",
  "    §8-12-18. It describes progress in each subject area above. Representative",
  "    samples of the child’s work and representative tests and assignments are",
  "    attached.",
] as const;

function testScoreLines(child: Child): string[] {
  return [
    `    ${child.grade} is one of the grades for which §8-12-18 asks for a score`,
    "    from a nationally normed standardized achievement test. Attach the score",
    "    report here.",
    ...(child.grade === "Grade 10" ? GRADE_10_NOTE : []),
  ];
}

function evidenceLines(child: Child): string[] {
  return [
    "4.  EVIDENCE OF SATISFACTORY PROGRESS",
    "",
    ...(isTestGrade(child.grade) ? testScoreLines(child) : PARENT_EVALUATION),
    "",
  ];
}

function logLines(program: Program, entryCount: number): string[] {
  const from = longDate(program.from) || "the start";
  const to = longDate(program.to) || "today";
  return [
    "5.  INSTRUCTIONAL LOG",
    "",
    `    ${entryCount} dated entries between ${from} and ${to}. The full log is attached.`,
    "",
    "",
    "_________________________________          ______________",
    "Parent signature                           Date",
  ];
}

export function buildAnnualReport(input: ReportInput): string {
  const list = entriesInProgram(input);
  const t = tally(list);
  return [
    ...headerLines(input, t),
    ...subjectLines(input.child, t),
    ...masteryLines(input.program),
    ...materialLines(input.program),
    ...evidenceLines(input.child),
    ...logLines(input.program, list.length),
  ].join("\n");
}
